import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { io } from 'socket.io-client';
import Layout from '../components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Enable fallback polling (set to false to disable polling completely)
const ENABLE_FALLBACK_POLLING = true;
const POLLING_INTERVAL_FAST = 1500; // 1.5 seconds when waiting for QR scan
const POLLING_INTERVAL_NORMAL = 5000; // 5 seconds when connected

export default function Dashboard({ user }) {
  const [status, setStatus] = useState('checking');
  const [qrCode, setQrCode] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const socketRef = useRef(null);
  const pollingInterval = useRef(null);
  
  // Use ref to track initializing state for polling (avoids stale closure issue)
  const isInitializingRef = useRef(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    isInitializingRef.current = isInitializing;
  }, [isInitializing]);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await axios.get(`${API}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionStatus(response.data);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };
    
    fetchSubscriptionStatus();
  }, []);

  // Initialize Socket.IO connection
  const initSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Don't create multiple connections
    if (socketRef.current?.connected) return;

    console.log('[Socket.IO] Connecting to:', BACKEND_URL);
    
    const socket = io(BACKEND_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });

    socket.on('connect', () => {
      console.log('[Socket.IO] Connected!');
      setSocketConnected(true);
      // Authenticate after connection
      socket.emit('authenticate', { token });
    });

    socket.on('authenticated', (data) => {
      console.log('[Socket.IO] Authenticated:', data);
    });

    socket.on('auth_error', (data) => {
      console.error('[Socket.IO] Auth error:', data);
      setSocketConnected(false);
    });

    // Real-time QR Code event
    socket.on('qr_code', (data) => {
      console.log('[Socket.IO] QR Code received - instant update!');
      setQrCode(data.qr);
      setStatus('qr_ready');
      setIsInitializing(false);
    });

    // Real-time WhatsApp connected event
    socket.on('whatsapp_connected', (data) => {
      console.log('[Socket.IO] WhatsApp connected:', data);
      setStatus('connected');
      setQrCode(null);
      setIsInitializing(false);
      if (data.phoneNumber) {
        setPhoneNumber(data.phoneNumber);
      }
    });

    // Real-time WhatsApp disconnected event
    socket.on('whatsapp_disconnected', (data) => {
      console.log('[Socket.IO] WhatsApp disconnected:', data);
      setStatus('disconnected');
      setQrCode(null);
      setPhoneNumber(null);
      setIsInitializing(false);
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket.IO] Disconnected:', reason);
      setSocketConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('[Socket.IO] Connection error:', error.message);
      setSocketConnected(false);
    });

    socketRef.current = socket;
  }, []);

  // Dynamic polling based on status
  useEffect(() => {
    // Initialize Socket.IO for real-time updates
    initSocket();
    
    // Initial status check
    checkStatus().then(() => {
      setInitialCheckDone(true);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [initSocket]);

  // Separate effect for polling with status dependency
  useEffect(() => {
    if (!ENABLE_FALLBACK_POLLING) return;
    
    // Clear any existing interval
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }
    
    // Use faster polling when waiting for connection (qr_ready, initializing)
    const needsFastPolling = status === 'qr_ready' || status === 'initializing' || isInitializing;
    const interval = needsFastPolling ? POLLING_INTERVAL_FAST : POLLING_INTERVAL_NORMAL;
    
    pollingInterval.current = setInterval(() => {
      checkStatus();
    }, interval);

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [status, isInitializing]);

  const checkStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const currentStatus = response.data.status;
      const newPhoneNumber = response.data.phoneNumber;
      
      // Use ref to get latest initializing state (avoids stale closure)
      const currentlyInitializing = isInitializingRef.current;
      
      // Debug log
      console.log('[Status Check]', { currentStatus, newPhoneNumber, prevStatus: status, isInitializing: currentlyInitializing });
      
      // IMPORTANT: Don't override initializing state with disconnected
      // This prevents race condition when user just clicked "Connect WhatsApp"
      // but backend hasn't created session yet
      if (currentlyInitializing && currentStatus === 'disconnected') {
        console.log('[Status] Ignoring disconnected during initialization');
        return; // Don't update anything - wait for proper status
      }
      
      // Handle connected state
      if (currentStatus === 'connected' || currentStatus === 'authenticated') {
        console.log('[Status] Connected! Clearing QR, setting phone:', newPhoneNumber);
        setStatus(currentStatus);
        setQrCode(null);
        setIsInitializing(false);
        setLoading(false);
        if (newPhoneNumber) {
          setPhoneNumber(newPhoneNumber);
        }
      }
      // Handle QR ready state
      else if (currentStatus === 'qr_ready') {
        // Always try to fetch QR when status is qr_ready
        // This handles cases where QR expired and needs refresh
        try {
          const qrResponse = await axios.get(`${API}/whatsapp/qr`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (qrResponse.data.qr) {
            setStatus('qr_ready');
            setQrCode(qrResponse.data.qr);
            setIsInitializing(false);
            setLoading(false); // QR received, stop loading
          } else {
            // QR not available - re-check status as user might have scanned
            // Don't immediately show disconnected - check if actually connected
            console.log('[Status] No QR in response, re-checking status...');
            const recheckResponse = await axios.get(`${API}/whatsapp/status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const recheckStatus = recheckResponse.data.status;
            
            if (recheckStatus === 'connected' || recheckStatus === 'authenticated') {
              // User scanned QR while we were fetching! Update to connected
              console.log('[Status] User connected while fetching QR!');
              setStatus(recheckStatus);
              setQrCode(null);
              setIsInitializing(false);
              setLoading(false);
              if (recheckResponse.data.phoneNumber) {
                setPhoneNumber(recheckResponse.data.phoneNumber);
              }
            } else if (!currentlyInitializing) {
              // Actually disconnected
              console.log('[Status] QR expired, showing disconnected');
              setQrCode(null);
              setStatus('disconnected');
              setLoading(false);
            }
          }
        } catch (error) {
          if (error.response?.status === 404) {
            // QR not available - re-check status as user might have scanned
            console.log('[Status] QR 404, re-checking status...');
            try {
              const recheckResponse = await axios.get(`${API}/whatsapp/status`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const recheckStatus = recheckResponse.data.status;
              
              if (recheckStatus === 'connected' || recheckStatus === 'authenticated') {
                console.log('[Status] User connected! (QR was 404 because scanned)');
                setStatus(recheckStatus);
                setQrCode(null);
                setIsInitializing(false);
                setLoading(false);
                if (recheckResponse.data.phoneNumber) {
                  setPhoneNumber(recheckResponse.data.phoneNumber);
                }
              } else if (!currentlyInitializing) {
                console.log('[Status] QR not found, showing disconnected');
                setQrCode(null);
                setStatus('disconnected');
                setLoading(false);
              }
            } catch (e) {
              // Ignore re-check errors
            }
          } else if (error.response?.status === 401) {
            handleTokenExpiry();
          }
        }
      }
      // Handle disconnected state (only if not initializing)
      else if (currentStatus === 'disconnected' && !currentlyInitializing) {
        setStatus('disconnected');
        setQrCode(null);
        setIsInitializing(false);
        setLoading(false);
        setPhoneNumber(null);
      }
      // Handle initializing state
      else if (currentStatus === 'initializing') {
        setStatus('initializing');
        setIsInitializing(true);
        // Keep loading true while initializing
      }
      
    } catch (error) {
      if (error.response?.status === 401) {
        handleTokenExpiry();
      }
    }
  };

  const handleTokenExpiry = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const handleInitialize = async () => {
    // Prevent double-click
    if (loading || isInitializing) {
      console.log('[Initialize] Already in progress, ignoring click');
      return;
    }
    
    setLoading(true);
    setIsInitializing(true);
    setStatus('initializing');
    setQrCode(null);
    setPhoneNumber(null);
    
    // Safety timeout - reset initializing state after 30 seconds if no QR
    const initTimeout = setTimeout(() => {
      console.log('[Initialize] Timeout - resetting state');
      setIsInitializing(false);
      setLoading(false);
      // Don't change status - let polling determine the actual state
    }, 30000);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[Initialize] Response:', response.data);
      
      if (response.data.status === 'connected' || response.data.status === 'already_connected') {
        clearTimeout(initTimeout);
        setStatus('connected');
        setIsInitializing(false);
        setLoading(false);
        if (response.data.phoneNumber) {
          setPhoneNumber(response.data.phoneNumber);
        }
      } else if (response.data.status === 'qr_ready' && response.data.has_qr) {
        // QR is already available, fetch it
        clearTimeout(initTimeout);
        setStatus('qr_ready');
        const qrResponse = await axios.get(`${API}/whatsapp/qr`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (qrResponse.data.qr) {
          setQrCode(qrResponse.data.qr);
        }
        setIsInitializing(false);
        setLoading(false);
      } else {
        // Initializing - keep loading true, let polling handle it
        // Timeout will auto-reset after 30 seconds if nothing happens
        console.log('[Initialize] Started, waiting for QR via polling (30s timeout)');
      }
    } catch (error) {
      clearTimeout(initTimeout);
      console.error('[Initialize] Error:', error);
      if (error.response?.status === 503) {
        alert('WhatsApp service is temporarily unavailable. Please try again.');
      }
      setStatus('disconnected');
      setIsInitializing(false);
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    const confirmed = window.confirm('Are you sure you want to disconnect WhatsApp? Session will be cleared and you will need to scan QR code again to connect a different account.');
    if (!confirmed) return;
    
    setLoading(true);
    setStatus('disconnecting');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }
      
      // Send logout: true to clear auth state and require new QR scan
      const response = await axios.post(`${API}/whatsapp/disconnect`, { logout: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Immediately update UI to disconnected
      setStatus('disconnected');
      setQrCode(null);
      setPhoneNumber(null);
      setIsInitializing(false);
      setLoading(false);
      
      // Optional: auto-refresh status after a short delay
      setTimeout(() => {
        checkStatus();
      }, 1000);
      
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        // Even on error, the backend might have cleared the session
        // So refresh status to get current state
        setStatus('disconnected');
        setQrCode(null);
        setPhoneNumber(null);
        setIsInitializing(false);
        setLoading(false);
        checkStatus();
      }
    }
  };

  const formatPhoneNumber = (num) => {
    if (!num) return '';
    // Format: +91 98765 43210
    if (num.startsWith('91') && num.length >= 12) {
      return `+${num.slice(0,2)} ${num.slice(2,7)} ${num.slice(7)}`;
    }
    return `+${num}`;
  };

  return (
    <Layout user={user}>
      <div className="page-header">
        <h1 data-testid="page-title">Dashboard</h1>
        <p>Welcome to BotWave - Your WhatsApp Automation Platform</p>
      </div>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        {/* Messages Today Card */}
        <div style={{
          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'white'
        }}>
          <p style={{ fontSize: '13px', opacity: 0.9, margin: '0 0 8px 0' }}>Messages Today</p>
          <p style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>
            {subscriptionStatus?.messages_today || 0}
          </p>
          <p style={{ fontSize: '13px', opacity: 0.8, margin: '8px 0 0 0' }}>
            {subscriptionStatus?.rate_limit_per_day > 0 ? `of ${subscriptionStatus.rate_limit_per_day} limit` : 'Unlimited'}
          </p>
        </div>

        {/* Subscription Card */}
        <div style={{
          background: subscriptionStatus?.plan === 'pro' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' :
                      subscriptionStatus?.plan === 'basic' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                      'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'white'
        }}>
          <p style={{ fontSize: '13px', opacity: 0.9, margin: '0 0 8px 0' }}>Current Plan</p>
          <p style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>{subscriptionStatus?.plan_name || 'Loading...'}</p>
          {subscriptionStatus?.plan !== 'free' && subscriptionStatus?.days_remaining !== null && (
            <p style={{ fontSize: '13px', opacity: 0.8, margin: '8px 0 0 0' }}>{subscriptionStatus?.days_remaining} days remaining</p>
          )}
        </div>

        {/* Sessions Card */}
        <div style={{
          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
          borderRadius: '12px',
          padding: '20px',
          color: 'white'
        }}>
          <p style={{ fontSize: '13px', opacity: 0.9, margin: '0 0 8px 0' }}>Max Sessions</p>
          <p style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>{subscriptionStatus?.max_sessions || 1}</p>
          <p style={{ fontSize: '13px', opacity: 0.8, margin: '8px 0 0 0' }}>WhatsApp accounts</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3>Quick Actions</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          <Link to="/whatsapp" style={{ textDecoration: 'none' }}>
            <div style={{
              padding: '20px',
              background: '#f0fdf4',
              borderRadius: '12px',
              border: '1px solid #bbf7d0',
              textAlign: 'center',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>📱</div>
              <p style={{ color: '#166534', fontWeight: '600', margin: '0 0 4px 0' }}>WhatsApp</p>
              <p style={{ color: '#22c55e', fontSize: '12px', margin: 0 }}>Manage Connections</p>
            </div>
          </Link>
          
          <Link to="/send-message" style={{ textDecoration: 'none' }}>
            <div style={{
              padding: '20px',
              background: '#eff6ff',
              borderRadius: '12px',
              border: '1px solid #bfdbfe',
              textAlign: 'center',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>✉️</div>
              <p style={{ color: '#1e40af', fontWeight: '600', margin: '0 0 4px 0' }}>Send Message</p>
              <p style={{ color: '#3b82f6', fontSize: '12px', margin: 0 }}>Send WhatsApp messages</p>
            </div>
          </Link>
          
          <Link to="/message-logs" style={{ textDecoration: 'none' }}>
            <div style={{
              padding: '20px',
              background: '#fef3c7',
              borderRadius: '12px',
              border: '1px solid #fcd34d',
              textAlign: 'center',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>📋</div>
              <p style={{ color: '#92400e', fontWeight: '600', margin: '0 0 4px 0' }}>Message Logs</p>
              <p style={{ color: '#f59e0b', fontSize: '12px', margin: 0 }}>View sent messages</p>
            </div>
          </Link>
          
          <Link to="/api-keys" style={{ textDecoration: 'none' }}>
            <div style={{
              padding: '20px',
              background: '#fae8ff',
              borderRadius: '12px',
              border: '1px solid #e879f9',
              textAlign: 'center',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔑</div>
              <p style={{ color: '#86198f', fontWeight: '600', margin: '0 0 4px 0' }}>API Keys</p>
              <p style={{ color: '#d946ef', fontSize: '12px', margin: 0 }}>Manage API access</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="card">
        <div className="card-header">
          <h3>Why BotWave?</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{ width: '48px', height: '48px', background: '#dcfce7', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>
              ⚡
            </div>
            <div>
              <h4 style={{ color: '#1e293b', margin: '0 0 4px 0', fontSize: '16px' }}>Instant Delivery</h4>
              <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Send WhatsApp messages instantly via API or web interface</p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{ width: '48px', height: '48px', background: '#dbeafe', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>
              🔒
            </div>
            <div>
              <h4 style={{ color: '#1e293b', margin: '0 0 4px 0', fontSize: '16px' }}>Secure & Reliable</h4>
              <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>End-to-end encryption with 99.9% uptime guarantee</p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{ width: '48px', height: '48px', background: '#fef3c7', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>
              📊
            </div>
            <div>
              <h4 style={{ color: '#1e293b', margin: '0 0 4px 0', fontSize: '16px' }}>Real-time Tracking</h4>
              <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Track message delivery status in real-time</p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{ width: '48px', height: '48px', background: '#fae8ff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>
              🔗
            </div>
            <div>
              <h4 style={{ color: '#1e293b', margin: '0 0 4px 0', fontSize: '16px' }}>Easy API Integration</h4>
              <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Simple REST API to integrate with your applications</p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{ width: '48px', height: '48px', background: '#f0fdf4', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>
              📱
            </div>
            <div>
              <h4 style={{ color: '#1e293b', margin: '0 0 4px 0', fontSize: '16px' }}>Multi-Session Support</h4>
              <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Connect multiple WhatsApp accounts based on your plan</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

